import { useRouter } from 'next/router';
import { useEffect, useState, useCallback } from 'react';

/**
 * Custom hook to track the previous URL in Next.js navigation
 *
 * This hook listens to route change events and maintains the previous URL,
 * which is useful for implementing "go back" functionality with a known fallback.
 *
 * @returns {string | null} The previous URL path, or null if no previous navigation
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const previousUrl = usePreviousUrl();
 *   const router = useRouter();
 *
 *   const handleBack = () => {
 *     // Use previous URL if available, otherwise use a fallback
 *     router.push(previousUrl || '/dashboard');
 *   };
 * };
 * ```
 *
 * @remarks
 * - The hook tracks the `asPath` which includes query parameters
 * - Returns `null` on initial load (no previous navigation)
 * - Updates only when navigating to a different URL
 */
export default function usePreviousUrl(): string | null {
  const router = useRouter();
  const [previousUrl, setPreviousUrl] = useState<string | null>(null);

  /**
   * Handler for route change events
   * Stores the current path as previous before navigating to the new path
   */
  const handleRouteChange = useCallback(
    (currentUrl: string) => {
      // Only update if we're actually navigating to a different URL
      if (currentUrl !== previousUrl && currentUrl !== router.asPath) {
        setPreviousUrl(router.asPath);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router],
  );

  useEffect(() => {
    // Subscribe to route change events
    router.events.on('routeChangeStart', handleRouteChange);

    // Cleanup: unsubscribe on unmount
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, handleRouteChange]);

  return previousUrl;
}
